import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { ArrowRight, Lock, Mail, KeyRound, ShieldAlert, CheckCircle2, Eye, EyeOff, Sparkles, ArrowLeft } from 'lucide-react';
import pulseLogo from '../assets/pulse-logo.png';
import { haptics } from '../lib/haptics';
import { sounds } from '../lib/sounds';

export function ResetPasswordPage() {
  const [adminEmail, setAdminEmail] = useState('');
  const [licenseKey, setLicenseKey] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLicenseKeyChange = (val: string) => {
    // Auto uppercase and trim
    setLicenseKey(val.toUpperCase().trim());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long.');
      haptics.notificationError();
      sounds.playAlertPing();
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match. Please re-enter both passwords.');
      haptics.notificationError();
      sounds.playAlertPing();
      return;
    }

    setLoading(true);
    haptics.tap();

    try {
      const res = await api.auth.resetPassword({
        adminEmail,
        licenseKey,
        newPassword,
      });

      if (res && res.token) {
        localStorage.setItem('pulse_token', res.token);
      }

      setSuccess(true);
      haptics.notificationSuccess();
      sounds.playSuccess();

      // Automatically sign in and navigate to admin portal after 2 seconds
      setTimeout(async () => {
        try {
          await login(adminEmail, newPassword);
          navigate('/admin', { replace: true });
        } catch {
          navigate('/login', { replace: true });
        }
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password. Please check your email and license key.');
      haptics.notificationError();
      sounds.playAlertPing();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#090909] text-white flex flex-col justify-center items-center px-4 py-8 selection:bg-pulse-blue selection:text-white">
      <div className="w-full max-w-md">
        {/* Brand Header */}
        <div className="text-center mb-7">
          <div className="inline-flex items-center justify-center mb-3">
            <img src={pulseLogo} alt="CK Pulse" className="w-14 h-14 object-contain drop-shadow-2xl" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-white">Recover Organization Access</h1>
          <p className="text-xs text-zinc-400 mt-1">
            Instant recovery using your Organization License Key
          </p>
        </div>

        {/* Recovery Card */}
        <div className="bg-[#121212] border border-[#222222] rounded-3xl p-6 sm:p-7 shadow-2xl relative overflow-hidden">
          {/* Subtle Ambient Glow */}
          <div className="absolute -top-12 -left-12 w-48 h-48 bg-pulse-blue/10 rounded-full blur-3xl pointer-events-none" />

          {success ? (
            <div className="py-6 text-center space-y-4 animate-scale-in">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 shadow-xl shadow-emerald-500/10">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-bold text-white tracking-tight">Password Reset Successfully!</h3>
                <p className="text-xs text-zinc-400">
                  Your credentials have been updated. Authenticating and opening your workspace...
                </p>
              </div>
              <div className="pt-2">
                <div className="w-5 h-5 mx-auto border-2 border-pulse-blue border-t-transparent rounded-full animate-spin" />
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 relative z-10">
              {error && (
                <div className="p-3 rounded-xl bg-rose-950/40 border border-rose-900/60 text-xs text-rose-300 flex items-start gap-2">
                  <ShieldAlert className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Failsafe Notice */}
              <div className="p-3 rounded-2xl bg-pulse-blue/10 border border-pulse-blue/20 text-[11px] text-zinc-300 flex items-start gap-2.5">
                <Sparkles className="w-4 h-4 text-pulse-blue shrink-0 mt-0.5" />
                <p className="leading-relaxed">
                  Enter your registered admin email and the <strong>License Key</strong> (<code>CK-XXXX-XXXX-...</code>) provided upon activation to instantly regain administrative control.
                </p>
              </div>

              {/* Email Input */}
              <div>
                <label className="block text-xs font-semibold text-zinc-300 mb-1.5">
                  Administrator Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-zinc-500">
                    <Mail className="w-4 h-4" />
                  </div>
                  <input
                    type="email"
                    required
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    placeholder="admin@company.com"
                    className="w-full pl-9 pr-3 py-2.5 text-xs rounded-xl pulse-input focus:outline-none"
                  />
                </div>
              </div>

              {/* License Key Input */}
              <div>
                <label className="block text-xs font-semibold text-zinc-300 mb-1.5">
                  Organization License Key
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-zinc-500">
                    <KeyRound className="w-4 h-4 text-amber-500/80" />
                  </div>
                  <input
                    type="text"
                    required
                    value={licenseKey}
                    onChange={(e) => handleLicenseKeyChange(e.target.value)}
                    placeholder="CK-XXXX-XXXX-XXXX-XXXX"
                    className="w-full pl-9 pr-3 py-2.5 text-xs font-mono uppercase tracking-wider rounded-xl pulse-input focus:outline-none"
                  />
                </div>
                <span className="text-[10px] text-zinc-500 mt-1 block">
                  Format: CK followed by 4-character alphanumeric blocks
                </span>
              </div>

              {/* New Password Input */}
              <div>
                <label className="block text-xs font-semibold text-zinc-300 mb-1.5">
                  New Secure Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-zinc-500">
                    <Lock className="w-4 h-4" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="•••••••••••• (min 8 characters)"
                    className="w-full pl-9 pr-9 py-2.5 text-xs rounded-xl pulse-input focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-zinc-500 hover:text-zinc-300"
                  >
                    {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password Input */}
              <div>
                <label className="block text-xs font-semibold text-zinc-300 mb-1.5">
                  Confirm New Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-zinc-500">
                    <Lock className="w-4 h-4" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••••••"
                    className="w-full pl-9 pr-3 py-2.5 text-xs rounded-xl pulse-input focus:outline-none"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 py-3 px-4 text-xs font-bold rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-lg shadow-blue-500/20 transition-all disabled:opacity-50 flex items-center justify-center gap-2 ios-press"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <span>Verify License & Reset Password</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Back to Login Link */}
        <div className="text-center mt-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Remembered your password? Back to sign in</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
